import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut, X, Home, Calendar, Spa, Settings, Comments, CreditCard } from './Icons';
import { useAuth } from '../context/AuthContext';
import { schedulesAPI, bookingsAPI } from '../services/api';
import { format, parseISO, isToday, isFuture, startOfDay, endOfDay } from 'date-fns';
import { useIsMobile } from '../hooks/useIsMobile';

// Alias icons for navigation
const DashboardIcon = Home;
const ScheduleIcon = Calendar;
const YogaIcon = Spa;
const SettingsIcon = Settings;
const CommunityIcon = Comments;
const SubscriptionIcon = CreditCard;

// Animated ring icon for the sidebar - eye-catching and enticing
// Uses fixed width container to match FontAwesome icons alignment
const AnimatedRingIcon = ({ className, style }) => (
  <span style={{ display: 'inline-block', width: '1.25em', textAlign: 'center', ...style }}>
    <svg
      viewBox="0 0 24 24"
      className={`${className} animate-ring-pulse`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', margin: '0 auto' }}
    >
    <defs>
      <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="var(--accent)" />
        <stop offset="50%" stopColor="var(--accent-hover)" />
        <stop offset="100%" stopColor="var(--accent)" />
      </linearGradient>
      <linearGradient id="innerGlow" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="var(--color-accent-100)" />
        <stop offset="100%" stopColor="var(--color-accent-200)" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    {/* Outer ring with gradient */}
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="url(#ringGradient)"
      strokeWidth="2.5"
      fill="none"
      filter="url(#glow)"
      className="origin-center"
    />
    {/* Inner decorative ring */}
    <circle
      cx="12"
      cy="12"
      r="5.5"
      stroke="url(#innerGlow)"
      strokeWidth="1.5"
      fill="none"
      opacity="0.8"
    />
      {/* Sparkle dots */}
      <circle cx="12" cy="3" r="1" fill="var(--accent-hover)" className="animate-twinkle" />
      <circle cx="21" cy="12" r="0.8" fill="var(--accent)" className="animate-twinkle-delayed" />
      <circle cx="12" cy="21" r="0.8" fill="var(--accent-hover)" className="animate-twinkle" />
      <circle cx="3" cy="12" r="0.8" fill="var(--accent)" className="animate-twinkle-delayed" />
    </svg>
  </span>
);

const baseNavItems = [
  { to: '/dashboard', icon: DashboardIcon, label: 'Dashboard' },
  { to: '/schedule', icon: ScheduleIcon, label: 'Studio Schedule' },
  { to: '/classes', icon: YogaIcon, label: 'Your Classes' },
  { to: '/rings', icon: AnimatedRingIcon, label: 'Earn Rings', highlight: true, requiresRings: true },
  // { to: '/community', icon: CommunityIcon, label: 'Community' }, // Disabled for now
  // { to: '/subscription', icon: SubscriptionIcon, label: 'Subscription' }, // Disabled for now
  { to: '/settings', icon: SettingsIcon, label: 'Settings' },
];

// Helper function to convert UUID to short ID (same as business Sidebar)
const getShortId = (id) => {
  if (!id) return null;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString().slice(-6).padStart(4, '0');
};

export default function CustomerSidebar({ onClose }) {
  const { studio, user, logout } = useAuth();
  const isMobile = useIsMobile();

  // Get studio ID - use actual ID from database
  const studioId = studio?.id || studio?.studioId;
  const studioName = studio?.name || 'Your Studio';
  const studioShortId = studioId ? getShortId(studioId) : null;

  // Fetch schedules for today to show count badge
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const todayDayOfWeek = today.getDay();

  const { data: todaySchedulesData } = useQuery({
    queryKey: ['schedules-today', studioId, format(today, 'yyyy-MM-dd')],
    queryFn: () => {
      // Fetch schedules for today's date range
      return schedulesAPI.getSchedules(studioId, {
        startDate: format(todayStart, 'yyyy-MM-dd'),
        endDate: format(todayEnd, 'yyyy-MM-dd')
      });
    },
    enabled: !!studioId,
    refetchInterval: false,
  });

  // Count schedules for today
  const todayScheduleCount = (() => {
    if (!todaySchedulesData?.data?.schedules) return 0;

    const schedules = todaySchedulesData.data.schedules;
    let count = 0;

    schedules.forEach(schedule => {
      // Check one-time schedules
      if (schedule.specificDate) {
        const scheduleDate = parseISO(schedule.specificDate);
        if (isToday(scheduleDate)) {
          count++;
        }
      }
      // Check weekly schedules
      else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
        if (schedule.dayOfWeek === todayDayOfWeek) {
          count++;
        }
      }
    });

    return count;
  })();

  // Fetch user's bookings to count upcoming classes
  const { data: myBookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false,
    refetchInterval: false,
  });

  // Count upcoming classes (future, or today but not yet started)
  const upcomingClassesCount = (() => {
    if (!myBookingsData?.data) return 0;

    const bookings = myBookingsData.data;
    let count = 0;

    bookings.forEach(booking => {
      const schedule = booking.schedule;
      if (!schedule) return;

      if (schedule.specificDate) {
        const classDate = parseISO(schedule.specificDate);

        // For today's classes, check if the class time has passed
        if (isToday(classDate)) {
          const startTime = schedule.startTime || '09:00:00';
          const [hours, minutes] = startTime.split(':');
          const classDateTime = new Date(classDate);
          classDateTime.setHours(parseInt(hours), parseInt(minutes), 0);
          if (classDateTime > new Date()) {
            count++;
          }
        } else if (isFuture(classDate)) {
          count++;
        }
      } else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
        // Recurring classes are considered upcoming
        count++;
      }
    });

    return count;
  })();

  return (
    <aside
      className="w-64 flex flex-col h-full relative"
      style={{
        background: 'var(--bg-surface)',
        borderRight: '1px solid var(--border-subtle)',
      }}
    >
      {/* Subtle side highlight */}
      <div
        className="absolute top-0 right-0 w-px h-full"
        style={{ background: 'linear-gradient(to bottom, var(--border-muted), transparent, transparent)' }}
      />

      {/* Logo/Brand Header */}
      <div
        className="relative p-5"
        style={{ borderBottom: '1px solid var(--border-subtle)' }}
      >
        {/* Subtle peach glow background */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, var(--primary-8) 0%, transparent 60%)' }}
        />

        <div className="relative flex items-center justify-between">
          {/* Logo Box */}
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, var(--primary-hover) 0%, var(--primary) 100%)',
                boxShadow: '0 4px 12px var(--primary-30), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              <span className="text-lg font-bold" style={{ color: 'var(--bg-base)' }}>M</span>
            </div>
            <div>
              <h1
                className="text-lg font-semibold"
                style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
              >
                My Studio Class
              </h1>
              <p
                className="text-xs truncate max-w-[120px]"
                style={{ color: 'var(--text-muted)' }}
              >
                {studioName}
              </p>
            </div>
          </div>

          {/* Close button for mobile */}
          {isMobile && onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all duration-200"
              style={{ color: 'var(--text-muted)' }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--primary-10)';
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-muted)';
              }}
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Studio ID Badge */}
        {studioShortId && (
          <div
            className="relative mt-3 px-3 py-1.5 rounded-lg text-center"
            style={{
              background: 'var(--highlight-8)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <span className="text-xs font-mono tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Studio ID: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{studioShortId}</span>
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {baseNavItems
          .filter(item => !item.requiresRings || studio?.settings?.ringsEnabled === true)
          .map((item, index) => {
          const Icon = item.icon;
          const showScheduleBadge = item.to === '/schedule' && todayScheduleCount > 0;
          const showClassesBadge = item.to === '/classes' && upcomingClassesCount > 0;
          const badgeCount = showScheduleBadge ? todayScheduleCount : (showClassesBadge ? upcomingClassesCount : 0);
          const isGoldItem = item.highlight;

          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => isMobile && onClose && onClose()}
              style={{
                animationDelay: `${index * 50}ms`,
              }}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 animate-fadeUp
                ${isActive
                  ? isGoldItem
                    ? 'font-medium'
                    : 'font-medium'
                  : ''
                }
              `}
            >
              {({ isActive }) => (
                <div
                  className="flex items-center gap-3 w-full rounded-xl"
                  style={{
                    margin: '-10px -16px',
                    padding: '10px 16px',
                    background: isActive
                      ? isGoldItem
                        ? 'linear-gradient(90deg, var(--highlight-15) 0%, var(--highlight-8) 100%)'
                        : 'var(--highlight-10)'
                      : 'transparent',
                    border: isActive ? '1px solid var(--border-subtle)' : '1px solid transparent',
                  }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200"
                    style={{
                      background: isActive
                        ? isGoldItem
                          ? 'var(--accent-15)'
                          : 'var(--primary-12)'
                        : 'transparent',
                    }}
                  >
                    <Icon
                      className="w-5 h-5"
                      style={{
                        color: isActive
                          ? isGoldItem
                            ? 'var(--accent)'
                            : 'var(--primary)'
                          : isGoldItem
                            ? 'var(--accent)'
                            : 'var(--text-muted)',
                      }}
                    />
                  </div>
                  <span
                    className="flex-1 whitespace-nowrap text-sm"
                    style={{
                      color: isActive
                        ? isGoldItem
                          ? 'var(--accent)'
                          : 'var(--text-primary)'
                        : isGoldItem
                          ? 'var(--accent)'
                          : 'var(--text-secondary)',
                    }}
                  >
                    {item.label}
                  </span>
                  {(showScheduleBadge || showClassesBadge) && (
                    <span
                      className="text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                      style={{
                        background: 'var(--primary)',
                        color: 'var(--bg-base)',
                      }}
                    >
                      {badgeCount}
                    </span>
                  )}
                </div>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl w-full transition-all duration-200"
          style={{ color: 'var(--text-muted)' }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            e.currentTarget.style.color = '#ef4444';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-muted)';
          }}
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center">
            <LogOut className="w-5 h-5" />
          </div>
          <span className="text-sm">Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
